import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, ArrowRight, CheckCircle, Clock, Lock, AlertCircle, History } from 'lucide-react';
import { format, isPast, parseISO, endOfDay } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompetitionsTab() {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [entries, setEntries] = useState({});
  const [entryStatuses, setEntryStatuses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompetitions();
  }, []);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
      
      // Check entries and entry status for each competition
      const entryPromises = response.data.map(comp => 
        axios.get(`${API}/entries/${comp.id}`).catch(() => null)
      );
      const statusPromises = response.data.map(comp =>
        axios.get(`${API}/competitions/${comp.id}/entry-status`).catch(() => null)
      );
      
      const [entryResponses, statusResponses] = await Promise.all([
        Promise.all(entryPromises),
        Promise.all(statusPromises)
      ]);
      
      const entriesMap = {};
      const statusMap = {};
      
      entryResponses.forEach((res, idx) => {
        if (res?.data) {
          entriesMap[response.data[idx].id] = res.data;
        }
      });
      
      statusResponses.forEach((res, idx) => {
        if (res?.data) {
          statusMap[response.data[idx].id] = res.data;
        }
      });
      
      setEntries(entriesMap);
      setEntryStatuses(statusMap);
    } catch (error) {
      console.error('Failed to load competitions:', error);
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = (competitionId) => {
    const status = entryStatuses[competitionId];
    const hasEntry = entries[competitionId]?.submittedAt;
    
    // If they have an entry, always let them view it
    if (hasEntry) {
      navigate(`/competition/${competitionId}`);
      return;
    }
    
    // If entries are not open, show message
    if (status && !status.canEnter) {
      toast.error(status.message);
      return;
    }
    
    navigate(`/competition/${competitionId}`);
  };

  if (loading) {
    return <div>Loading competitions...</div>;
  }

  // Helper to check if a competition has ended
  const isCompetitionPast = (comp) => {
    const endDate = comp.endsOn || comp.startsOn;
    if (!endDate) return false;
    // Competition ends at the end of the last day (23:59:59)
    return isPast(endOfDay(parseISO(endDate)));
  };

  // Split competitions into current/upcoming and past
  // Only show past competitions if the club has entered them
  const currentCompetitions = competitions.filter(comp => !isCompetitionPast(comp));
  const pastCompetitions = competitions.filter(comp => isCompetitionPast(comp) && entries[comp.id]?.submittedAt);

  // Render a competition card
  const renderCompetitionCard = (comp, isPast = false) => {
    const hasEntry = entries[comp.id]?.submittedAt;
    const entryStatus = entryStatuses[comp.id];
    const isNotStarted = entryStatus?.status === 'not_started';
    const isClosed = entryStatus?.status === 'closed';
    const isNotEligible = entryStatus?.status === 'not_eligible';
    
    // Determine the status to display
    let statusBadge = null;
    let headerColor = 'bg-gradient-to-r from-[#000000] to-gray-700';
    
    if (isPast && hasEntry) {
      statusBadge = (
        <span className="bg-gray-600 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm flex items-center gap-1">
          <History className="w-3 h-3" />
          COMPLETED
        </span>
      );
      headerColor = 'bg-gradient-to-r from-gray-600 to-gray-700';
    } else if (hasEntry) {
      statusBadge = (
        <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm">
          ENTERED
        </span>
      );
      headerColor = 'bg-gradient-to-r from-green-500 to-green-600';
    } else if (isNotStarted) {
      statusBadge = (
        <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm flex items-center gap-1">
          <Clock className="w-3 h-3" />
          COMING SOON
        </span>
      );
      headerColor = 'bg-gradient-to-r from-blue-500 to-blue-600';
    } else if (isClosed || !comp.isOpen) {
      statusBadge = (
        <span className="bg-gray-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm flex items-center gap-1">
          <Lock className="w-3 h-3" />
          CLOSED
        </span>
      );
      headerColor = 'bg-gradient-to-r from-gray-500 to-gray-600';
    } else if (isNotEligible) {
      statusBadge = (
        <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm">
          NOT ELIGIBLE
        </span>
      );
      headerColor = 'bg-gradient-to-r from-orange-500 to-orange-600';
    } else {
      statusBadge = (
        <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm">
          OPEN
        </span>
      );
    }
    
    return (
      <Card 
        key={comp.id} 
        data-testid={`competition-card-${comp.id}`} 
        className={`hover:shadow-xl transition-all duration-200 border-2 overflow-hidden ${
          isPast ? 'border-gray-200 bg-gray-50/50 opacity-90' :
          hasEntry ? 'border-green-200 bg-green-50/30' : 
          isNotStarted ? 'border-blue-200 bg-blue-50/30' :
          isClosed ? 'border-gray-200 bg-gray-50/30' :
          isNotEligible ? 'border-orange-200 bg-orange-50/30' :
          'border-gray-200 hover:border-[#000000]'
        }`}
      >
        {/* Header Banner */}
        <div className={`h-2 ${headerColor}`} />
        
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <CardTitle className="text-xl sm:text-2xl leading-tight flex-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {comp.title}
            </CardTitle>
            {statusBadge}
          </div>

          {/* Date & Location */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar className="w-4 h-4 text-[#000000] flex-shrink-0" />
              <span className="text-sm font-medium">
                {comp.displayDate || `${format(new Date(comp.startsOn), 'dd MMM yyyy')} - ${format(new Date(comp.endsOn), 'dd MMM yyyy')}`}
              </span>
            </div>
            
            {comp.location && (
              <div className="flex items-center gap-2 text-gray-600">
                <svg className="w-4 h-4 text-[#000000] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm">{comp.location}</span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-4">
          {/* Status Message - Only for current competitions */}
          {!isPast && isNotStarted && entryStatus?.message && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-blue-800 font-medium">{entryStatus.message}</p>
              </div>
              {comp.entryStartDate && (
                <p className="text-xs text-blue-600 mt-1">
                  Entries open: {format(new Date(comp.entryStartDate), 'dd MMM yyyy')}
                  {comp.entryStartTime && ` at ${comp.entryStartTime}`}
                </p>
              )}
            </div>
          )}
          
          {!isPast && (isClosed || (!comp.isOpen && !hasEntry)) && entryStatus?.message && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-600" />
                <p className="text-sm text-gray-700 font-medium">{entryStatus.message}</p>
              </div>
            </div>
          )}
          
          {!isPast && isNotEligible && entryStatus?.message && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <p className="text-sm text-orange-800 font-medium">{entryStatus.message}</p>
              </div>
            </div>
          )}
        
          {/* Closing Date Info - Only show if entries are open and not past */}
          {!isPast && comp.closingDate && !isClosed && !isNotStarted && entryStatus?.canEnter && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Entry Deadline</p>
              <p className="text-sm text-amber-900 font-medium">
                {format(new Date(comp.closingDate), 'EEEE, do MMMM yyyy')}
                {comp.closingTime && ` at ${comp.closingTime}`}
              </p>
            </div>
          )}

          {/* Entry Fee - Only show for current competitions */}
          {!isPast && (
            <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wide mb-1">Entry Fee</p>
              <p className="text-3xl font-bold text-[#000000]">£{comp.entryFee}</p>
              <p className="text-xs text-gray-500 mt-1">per gymnast</p>
            </div>
          )}

          {/* Action Button */}
          <Button
            data-testid={`enter-competition-button-${comp.id}`}
            onClick={() => handleEnter(comp.id)}
            disabled={!hasEntry && !entryStatus?.canEnter}
            className={`w-full font-semibold py-6 text-base transition-all ${
              isPast 
                ? 'bg-gray-600 hover:bg-gray-700 shadow-md'
                : hasEntry 
                ? 'bg-green-600 hover:bg-green-700 shadow-md' 
                : isNotStarted
                ? 'bg-blue-400 cursor-not-allowed'
                : isClosed || isNotEligible
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#000000] hover:bg-[#333333] shadow-md hover:shadow-lg'
            }`}
          >
            {isPast && hasEntry ? (
              <>
                <History className="w-5 h-5 mr-2" />
                View Entry
              </>
            ) : hasEntry ? (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                View Your Entry
              </>
            ) : isNotStarted ? (
              <>
                <Clock className="w-5 h-5 mr-2" />
                Entries Not Yet Open
              </>
            ) : isClosed ? (
              <>
                <Lock className="w-5 h-5 mr-2" />
                Entries Closed
              </>
            ) : isNotEligible ? (
              <>
                <AlertCircle className="w-5 h-5 mr-2" />
                Not Eligible
              </>
            ) : (
              <>
                Enter Competition
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          {/* View Programme Button - Only show if entry exists */}
          {hasEntry && (
            <Button
              onClick={() => navigate(`/programme/${comp.id}`)}
              variant="outline"
              className="w-full font-semibold py-4 text-base border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white transition-all"
            >
              View Programme
            </Button>
          )}

          {/* Submitted Badge */}
          {hasEntry && (
            <div className={`${isPast ? 'bg-gray-100 border-gray-300' : 'bg-green-100 border-green-300'} border rounded-lg p-3 text-center`}>
              <p className={`text-sm ${isPast ? 'text-gray-800' : 'text-green-800'} font-semibold`}>
                ✓ Entry {isPast ? 'Was' : ''} Submitted
              </p>
              <p className={`text-xs ${isPast ? 'text-gray-700' : 'text-green-700'} mt-1`}>
                on {format(new Date(entries[comp.id].submittedAt), 'dd/MM/yyyy HH:mm')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      {/* Current & Upcoming Competitions */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-center mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Available Competitions
        </h2>
        <p className="text-center text-gray-600 text-sm">
          Select a competition to enter or view your existing entry
        </p>
      </div>

      {currentCompetitions.length === 0 ? (
        <Card className="border-2 border-dashed mb-12">
          <CardContent className="py-16 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg font-medium">No open competitions available at the moment.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for upcoming competitions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
          {currentCompetitions.map(comp => renderCompetitionCard(comp, false))}
        </div>
      )}

      {/* Past Competitions - Only show if club has entered any */}
      {pastCompetitions.length > 0 && (
        <>
          <div className="mb-8 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-center mb-2 text-gray-600" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              <History className="w-6 h-6 inline-block mr-2 mb-1" />
              Past Competitions
            </h2>
            <p className="text-center text-gray-500 text-sm">
              View your previously entered competitions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {pastCompetitions.map(comp => renderCompetitionCard(comp, true))}
          </div>
        </>
      )}
    </div>
  );
}